"use client";

import { spanishChromeLabel, translationSourceHash } from "@repo/questionnaire";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { cn } from "@/lib/utils";

import type { QuestionnaireFieldRendererProps } from "./questionnaire-field-renderer-types";
export function QuestionnaireChoicesControl({
  field,
  value,
  onChange,
  disabled,
  locale,
  translations,
}: QuestionnaireFieldRendererProps) {
  const translate = (text: string) =>
    locale === "en"
      ? text
      : (translations?.[translationSourceHash(text)] ??
        (locale === "es" ? spanishChromeLabel(text) : text));
  if (field.kind === "enum") {
    return (
      <div data-questionnaire-options className="grid grid-cols-1 gap-2">
        {field.options.map((option) => {
          const selected = value === option.id;
          return (
            <Button
              key={option.id}
              type="button"
              variant={selected ? "default" : "outline"}
              className="h-auto min-h-12 justify-start whitespace-normal px-4 py-3 text-left text-base"
              disabled={disabled}
              aria-pressed={selected}
              data-questionnaire-choice="true"
              onClick={() => onChange(option.id)}
            >
              <span>
                <span className="block font-medium">
                  {translate(option.label)}
                </span>
                {option.description && (
                  <span
                    className={cn(
                      "mt-1 block text-xs",
                      selected
                        ? "text-primary-foreground/75"
                        : "text-muted-foreground",
                    )}
                  >
                    {translate(option.description)}
                  </span>
                )}
              </span>
            </Button>
          );
        })}
      </div>
    );
  }
  if (field.kind === "multi_select") {
    const selected = Array.isArray(value)
      ? value.filter((item): item is string => typeof item === "string")
      : [];
    return (
      <div data-questionnaire-options className="grid grid-cols-1 gap-2">
        {field.options.map((option) => {
          const checked = selected.includes(option.id);
          const optionId = `${field.id}-option-${option.id}`;
          return (
            <label
              key={option.id}
              htmlFor={optionId}
              className={cn(
                "flex min-h-14 cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 text-left text-base leading-6 transition-colors hover:bg-muted/40 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2",
                checked ? "border-foreground bg-muted/40" : "border-input",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <Checkbox
                id={optionId}
                checked={checked}
                disabled={disabled}
                className="mt-0.5 rounded-[4px] border-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
                onCheckedChange={(next) =>
                  onChange(
                    next === true
                      ? [...selected, option.id]
                      : selected.filter((item) => item !== option.id),
                  )
                }
              />
              <span className="min-w-0">
                <span className="block font-medium">
                  {translate(option.label)}
                </span>
                {option.description && (
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {translate(option.description)}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    );
  }
  if (field.kind === "yes_no") {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: field.yesLabel ?? "Yes", answer: true },
          { label: field.noLabel ?? "No", answer: false },
        ].map((option) => (
          <Button
            key={String(option.answer)}
            type="button"
            variant={value === option.answer ? "default" : "outline"}
            className="h-12"
            disabled={disabled}
            aria-pressed={value === option.answer}
            data-questionnaire-choice="true"
            onClick={() => onChange(option.answer)}
          >
            {translate(option.label)}
          </Button>
        ))}
      </div>
    );
  }
  if (field.kind === "consent") {
    return (
      <label
        htmlFor={`${field.id}-consent`}
        className="flex items-start gap-3 rounded-xl border p-4"
      >
        <Checkbox
          id={`${field.id}-consent`}
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <span className="text-sm leading-relaxed text-muted-foreground">
          {field.body}
        </span>
      </label>
    );
  }
  return null;
}
